import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

dayjs.extend(duration);

export const formatDateWithoutTime = (
  dateString: any,
  options?: Intl.DateTimeFormatOptions,
  locale: string = "en-GB",
) => {
  if (!dateString) return "-";

  const parsedDate = new Date(dateString);
  if (isNaN(parsedDate.getTime())) return "-";

  const finalOptions: Intl.DateTimeFormatOptions = options || {
    day: "numeric",
    month: "numeric",
    year: "numeric",
  };

  return parsedDate.toLocaleString(locale, finalOptions);
};

export const formatDate = (
  dateString: any,
  showSeconds = false,
  showRelativeDate = false,
) => {
  if (!dateString) return "-";

  const parsedDate = new Date(dateString);
  if (isNaN(parsedDate.getTime())) return "-";

  if (showRelativeDate) {
    const dateResult = parsedDate.toLocaleString("en-GB", {
      day: "numeric",
      month: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: showSeconds ? "2-digit" : undefined,
    });
    return dateResult + "\n" + formatRelativeDate(dateString);
  } else {
    return parsedDate.toLocaleString("en-GB", {
      day: "numeric",
      month: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: showSeconds ? "2-digit" : undefined,
    });
  }
};

/**
 * Returns "Today", "Yesterday", "X Days Ago" (2–6), "1 Week Ago" (7 days),
 * or a normal date without time for older dates.
 */
export const formatRelativeDate = (dateString: any) => {
  if (!dateString) return "-";
  const date = dayjs(dateString);
  if (!date.isValid()) return "-";
  const today = dayjs().startOf("day");
  const inputDay = date.startOf("day");
  const daysDiff = today.diff(inputDay, "day");
  if (daysDiff < 0) return "-";
  if (daysDiff === 0) return "Today";
  if (daysDiff === 1) return "Yesterday";
  return `${daysDiff} Days Ago`;
  // if (daysDiff === 7) return "1 Week Ago";
  // return formatDateWithoutTime(dateString);
};

/**
 * Returns only the time portion (e.g. "14:30" or "02:30 pm" depending on locale).
 */
export const formatTimeOnly = (dateString: any) => {
  if (!dateString) return "-";
  const parsedDate = new Date(dateString);
  if (isNaN(parsedDate.getTime())) return "-";
  return parsedDate.toLocaleString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatMinutes = (input: any) => {
  const inputInt = parseInt(input);

  if (inputInt && input > 0) {
    const time = dayjs.duration(inputInt, "minutes");

    return time.format("H [hr] m [min]");
  } else {
    return "-";
  }
};

export function normalizeIds(Ids: any): string | null {
  if (!Ids) return null;

  let result: string[] = [];

  if (Array.isArray(Ids) && typeof Ids[0] === "object") {
    result = Ids.map((item: any) => item?.id?.toString()).filter(Boolean);
  } else if (Array.isArray(Ids)) {
    result = Ids.map((id) => id?.toString()).filter(Boolean);
  } else if (typeof Ids === "string") {
    result = Ids.split(",")
      .map((id) => id.trim())
      .filter(Boolean);
  }

  return result.length > 0 ? result.join(",") : null;
}

export function formatAmount(value: number | string): string {
  if (value === null || value === undefined || isNaN(Number(value))) return "-";

  const amount = parseFloat(value.toString());

  if (amount >= 1_00_00_000) {
    return (amount / 1_00_00_000).toFixed(2).replace(/\.00$/, "") + " Cr";
  }

  if (amount >= 1_00_000) {
    return (amount / 1_00_000).toFixed(2).replace(/\.00$/, "") + " L";
  }

  if (amount >= 1_000) {
    return (amount / 1_000).toFixed(2).replace(/\.00$/, "") + " K";
  }

  return amount.toFixed(2).replace(/\.00$/, "");
}

export function formatNumber(value: number | string): string {
  const num = Number(value);
  if (isNaN(num)) return "-";

  if (Number.isInteger(num)) {
    return num.toString();
  }

  return num.toFixed(2).replace(/(\.\d)0$/, "$1"); // e.g. 12.30 → 12.3
}
